/**
 * One shared copy of the caller's folder tree (the Deal Room).
 *
 * Every screen that shows deals used to fetch the whole tree on mount, and the
 * "files updated" event made each of them fetch it again. Opening the Deal Room
 * chained three or four identical requests (the first-run gate, the Files
 * home, the sidebar), each paid in full. This store makes them share:
 *
 *   - a request already in flight is joined, never repeated;
 *   - a tree fetched in the last `FRESH_MS` is served as is, so moving from one
 *     Deal Room page to the next renders at once instead of re-asking;
 *   - `USER_FILES_UPDATED_EVENT` starts ONE fresh request here, and the
 *     screens' own handlers for that event join it.
 *
 * Only the newest request writes the snapshot, so an older answer landing late
 * cannot bring back a deleted file or hide a new one.
 */
import { USER_FILES_UPDATED_EVENT } from "../constants/events";
import { type FolderTreeNode, getUserFolderTree } from "./fileApi";

/** How long a fetched tree is served without asking the server again. */
export const FRESH_MS = 3000;

export interface FolderTreeSnapshot {
	/** Null until the first request answers. */
	tree: FolderTreeNode[] | null;
	/** Set when the newest request failed; the last good tree stays. */
	error: unknown;
}

const EMPTY: FolderTreeSnapshot = { tree: null, error: null };

let snapshot: FolderTreeSnapshot = EMPTY;
let fetchedAt = 0;
let latestRequest = 0;
/** The request whose answer the snapshot holds. */
let publishedRequest = 0;
let inflight: Promise<FolderTreeNode[]> | null = null;
const listeners = new Set<() => void>();

function publish(next: FolderTreeSnapshot): void {
	snapshot = next;
	for (const listener of listeners) listener();
}

function startRequest(): Promise<FolderTreeNode[]> {
	const request = ++latestRequest;
	const promise = getUserFolderTree().then(
		(folders): FolderTreeNode[] | Promise<FolderTreeNode[]> => {
			const tree = Array.isArray(folders) ? folders : [];
			if (request === latestRequest) {
				fetchedAt = Date.now();
				publishedRequest = request;
				publish({ tree, error: null });
				return tree;
			}
			// Superseded: callers awaiting this request get the newer answer
			// too, never this older one — the newer request still in flight,
			// or the tree it already published.
			if (inflight && inflight !== promise) return inflight;
			if (publishedRequest > request && snapshot.tree !== null) {
				return snapshot.tree;
			}
			return tree;
		},
		(error: unknown) => {
			if (request === latestRequest) {
				publish({ tree: snapshot.tree, error });
			}
			throw error;
		},
	);
	inflight = promise;
	const clear = () => {
		if (inflight === promise) inflight = null;
	};
	promise.then(clear, clear);
	return promise;
}

/**
 * The tree, from the request in flight or a recent answer when there is one.
 * `fresh: true` always asks the server — use it right after a change the
 * caller made itself (a delete), where an earlier request cannot know about it.
 */
export function loadFolderTree(
	options: { fresh?: boolean } = {},
): Promise<FolderTreeNode[]> {
	if (!options.fresh) {
		if (inflight) return inflight;
		if (
			snapshot.tree !== null &&
			!snapshot.error &&
			Date.now() - fetchedAt < FRESH_MS
		) {
			return Promise.resolve(snapshot.tree);
		}
	}
	return startRequest();
}

export function getFolderTreeSnapshot(): FolderTreeSnapshot {
	return snapshot;
}

export function subscribeFolderTree(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** Forget everything — on sign-out, so the next user never sees this tree. */
export function resetFolderTreeStore(): void {
	eventRequest = null;
	trailingRefresh = false;
	latestRequest += 1;
	publishedRequest = 0;
	inflight = null;
	fetchedAt = 0;
	publish(EMPTY);
}

/**
 * Refetch after a files-updated event. A burst of events (several uploads
 * finishing together) costs at most two requests: the one already running for
 * an earlier event, then ONE trailing request for everything after it.
 */
let eventRequest: Promise<FolderTreeNode[]> | null = null;
let trailingRefresh = false;

function refreshForEvent(): void {
	if (eventRequest && inflight === eventRequest) {
		trailingRefresh = true;
		return;
	}
	const request = loadFolderTree({ fresh: true });
	eventRequest = request;
	request
		.catch(() => {
			// Recorded on the snapshot; the screens show it.
		})
		.finally(() => {
			if (eventRequest === request) eventRequest = null;
			if (trailingRefresh) {
				trailingRefresh = false;
				refreshForEvent();
			}
		});
}

// Registered when the module loads, before any screen mounts, so on every
// update this listener runs first and the screens' handlers join its request.
if (typeof window !== "undefined") {
	window.addEventListener(USER_FILES_UPDATED_EVENT, refreshForEvent);
}
